import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Breadcrumb, Col, Modal, Pagination, Row, Empty, Spin } from "antd";
import Footer from "../component/Footer";
import { useCookies } from "react-cookie";
import { useDevice } from "../hooks";
import { BookOutlined, ArrowRightOutlined, UserOutlined, HomeOutlined, AlignLeftOutlined, OrderedListOutlined, ReadOutlined } from "@ant-design/icons";
import logo from '../component/image/logo.png';
import LearningGoal from '../component/LearningGoal';

export default function Course() {
    useDevice();

    const { slug } = useParams();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalSpeakOpen, setIsModalSpeakOpen] = useState(false);
    const [dataCourse, setdataCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 12 });
    const [cookies] = useCookies(["user"]);
    const navigate = useNavigate();
    const [displayOpts, setDisplayOpts] = useState({
        showDescription: true,
        showLessonCount: true,
        showAuthor:      true,
        showIndex:       true,
    });

    const toggle = (key) => setDisplayOpts(prev => ({ ...prev, [key]: !prev[key] }));
    const course = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/course/getBySlugTopic/${slug}`, { params: pagination });
            setdataCourse(response?.data[0]);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        course();
    }, [slug, pagination]);

    const colors = [
        'from-blue-500 to-blue-700',
        'from-green-500 to-emerald-700',
        'from-purple-500 to-purple-700',
        'from-orange-500 to-red-600',
        'from-pink-500 to-rose-700',
        'from-cyan-500 to-teal-700',
        'from-indigo-500 to-indigo-700',
        'from-amber-500 to-orange-600',
    ];

    return (
        <>
            <div className="min-h-screen bg-[#0a1628]">
                {/* Hero Section with Topic Info */}
                <div className="bg-gradient-to-r from-[#0f1e35] to-[#1a2d4a] text-white relative overflow-hidden border-b border-white/10">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                    <div className="max-w-screen-xl mx-auto px-6 py-12 relative z-10">
                        <Breadcrumb
                            className="mb-6 [&_.ant-breadcrumb-link]:text-blue-300 [&_.ant-breadcrumb-separator]:text-blue-400 [&_a]:text-blue-300 [&_a:hover]:text-white"
                            items={[
                                { title: <Link to="/"><HomeOutlined /> Trang chủ</Link> },
                                { title: <Link to="/topic">Chủ đề</Link> },
                                { title: <span className="text-white font-medium">{dataCourse?.title}</span> },
                            ]}
                        />
                        <div className="flex items-center gap-6">
                            {(
                                <div className="hidden md:block flex-shrink-0 w-28 h-28 rounded-2xl overflow-hidden border-4 border-white/20 shadow-lg">
                                    <img src={dataCourse?.image_url || logo} alt={dataCourse?.title} className="w-full h-full object-cover" crossOrigin="anonymous" onError={(e) => { e.target.onerror = null; e.target.src = logo; }} />
                                </div>
                            )}
                            <div>
                                <h1 className="text-3xl md:text-4xl font-extrabold mb-2">{dataCourse?.title || 'Đang tải...'}</h1>
                                <p className="text-blue-300 text-lg">
                                    {dataCourse?.course?.length || 0} khóa học có sẵn
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-screen-xl mx-auto px-6 py-10">
                    {/* Learning Goal */}
                    <LearningGoal />

                    {/* Display Settings Bar */}
                    <div className="mb-8 bg-[#0f1e35] border border-white/10 rounded-2xl px-6 py-4">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Tuỳ chỉnh hiển thị</p>
                        <div className="flex flex-wrap gap-3">
                            {[
                                { key: 'showDescription', icon: <AlignLeftOutlined />,   label: 'Mô tả' },
                                { key: 'showLessonCount', icon: <ReadOutlined />,         label: 'Số bài học' },
                                { key: 'showAuthor',      icon: <UserOutlined />,         label: 'Tác giả' },
                                { key: 'showIndex',       icon: <OrderedListOutlined />,  label: 'Số thứ tự' },
                            ].map(({ key, icon, label }) => (
                                <button
                                    key={key}
                                    onClick={() => toggle(key)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                                        displayOpts[key]
                                            ? 'bg-blue-500 text-white border-blue-500 shadow-md shadow-blue-500/20'
                                            : 'bg-white/5 text-gray-400 border-white/10 hover:border-blue-400 hover:text-blue-400'
                                    }`}
                                >
                                    {icon}
                                    {label}
                                    {displayOpts[key] && (
                                        <span className="w-2 h-2 rounded-full bg-white/70 ml-0.5"></span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-20"><Spin size="large" /></div>
                    ) : !dataCourse?.course || dataCourse.course.length === 0 ? (
                        <div className="py-20"><Empty description={<span className="text-gray-400">Chưa có khóa học nào trong chủ đề này</span>} /></div>
                    ) : (
                        <>
                            {/* Course Grid */}
                            <Row gutter={[24, 24]}>
                                {dataCourse?.course?.map((c, index) => (
                                    <Col xs={24} sm={12} md={8} lg={6} key={index}>
                                        <Link to={'/lesson/' + c.slug} className="block group h-full">
                                            <div className="bg-[#0f1e35] border border-white/10 rounded-xl hover:border-white/20 transition-all duration-300 overflow-hidden h-full flex flex-col group-hover:-translate-y-1">
                                                {/* Color Header */}
                                                <div className={`bg-gradient-to-br ${colors[index % colors.length]} p-6 text-white relative`}>
                                                    <div className="absolute top-3 right-3 bg-white/20 rounded-full w-10 h-10 flex items-center justify-center">
                                                        <BookOutlined className="text-lg" />
                                                    </div>
                                                    {displayOpts.showIndex && (
                                                        <div className="text-3xl font-extrabold opacity-20 absolute bottom-2 right-4">
                                                            {String(index + 1).padStart(2, '0')}
                                                        </div>
                                                    )}
                                                    <h3 className="text-lg font-bold pr-12 line-clamp-2 relative z-10">{c.title}</h3>
                                                </div>

                                                {/* Card Body */}
                                                <div className="p-5 flex flex-col flex-1">
                                                    {displayOpts.showDescription && c.description && (
                                                        <p className="text-gray-400 text-sm line-clamp-2 mb-4">{c.description}</p>
                                                    )}
                                                    <div className="mt-auto">
                                                        {displayOpts.showAuthor && c.username && (
                                                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                                                                <UserOutlined />
                                                                <span>{c.username}</span>
                                                            </div>
                                                        )}
                                                        <div className="flex items-center justify-between">
                                                            {displayOpts.showLessonCount ? (
                                                                <span className="text-xs bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full">
                                                                    {c.totalLessons || '—'} bài học
                                                                </span>
                                                            ) : <span />}
                                                            <span className="text-blue-400 group-hover:text-blue-300 group-hover:translate-x-1 transition-all">
                                                                <ArrowRightOutlined />
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    </Col>
                                ))}
                            </Row>

                            {dataCourse?.total > pagination.pageSize && (
                                <div className="flex justify-center mt-12">
                                    <Pagination
                                        current={pagination.page}
                                        total={dataCourse?.total}
                                        pageSize={pagination.pageSize}
                                        showSizeChanger={false}
                                        onChange={(p, ps) => setPagination({ page: p, pageSize: ps })}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <Footer />

            <Modal open={isModalOpen}
                onOk={() => cookies?.user ? navigate(`/profile/${cookies.user.id}`) : navigate('/login')}
                onCancel={() => setIsModalOpen(false)} okButtonProps={{ className: "bg-blue-500" }}>
                <p className="text-xl font-bold py-2 pt-8">Vui lòng truy cập vào profile và </p>
                <p className="text-xl font-bold py-2 pb-8">đăng ký gói vip để được sử dụng dịch vụ này</p>
            </Modal>

            <Modal open={isModalSpeakOpen} onOk={() => setIsModalSpeakOpen(false)}
                onCancel={() => setIsModalSpeakOpen(false)} okButtonProps={{ className: "bg-blue-500" }}>
                <p className="text-xl font-bold py-5 pt-8">Vui lòng tải app để được dùng chức năng này!</p>
            </Modal>
        </>
    );
};
